import React, { useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { useQuery, useLazyQuery } from 'react-apollo';
import {
  RELATORIO_VENDAS,
  RELATORIO_VENDAS_CSV,
} from '../../../graphql/queryes';
import List from '../../../components/list/list';
import Search from './components/search';
import ButtonFloat from '../../../components/button/ButtonFloat';
import { ArrowDownloadIcon } from '../../../components/icons/ArrowDownload';

const columns = [
  {
    title: 'ID',
    value: (item) => item.id,
  },
  {
    title: 'Data',
    value: (item) =>
      item.dataLancamento
        ? moment(item.dataLancamento).format('DD/MM/YYYY')
        : '',
  },
  {
    title: 'Nome',
    value: (item) => item.unidade?.nomeFantasia,
  },
  {
    title: 'Cidade',
    value: (item) => item.unidade?.municipio?.nome,
  },
  {
    title: 'UF',
    value: (item) => item.unidade?.municipio?.uf,
  },
  {
    title: 'Endereço',
    value: (item) => {
      const endereco = item.unidade?.endereco;
      if (!endereco) return '';
      return [endereco.nomeLogradouro, endereco.numero, endereco.bairro]
        .filter(Boolean)
        .join(', ');
    },
  },
  {
    title: 'Telefone',
    value: (item) => item.unidade?.telefonePrincipal,
  },
  {
    title: 'Status',
    value: (item) => {
      const status = {
        ATIVA: 'Ativa',
        CADASTRO_PARCIAL: 'Cadastro Parcial',
        CANCELADA: 'Cancelada',
        FINALIZADA: 'Finalizada',
        PAGAMENTO_RECUSADO: 'Pagamento Recusado',
        PERIODO_GRATIS: 'Período Grátis',
      };

      return status[item.status];
    },
  },
];

const pageableDTODefault = {
  pageNumber: 0,
  pageSize: 30,
  sortDir: 'DESC',
  sortField: 'dataLancamento',
};

const filtersDefault = {
  dataInicial: moment().startOf('month').format('YYYY-MM-DD'),
  dataFinal: moment().endOf('month').format('YYYY-MM-DD'),
  statusList: [],
};

const RelatorioVendas = () => {
  const [filters, setFilters] = useState({ ...filtersDefault });

  const searchDTO = {
    dataInicial: filters.dataInicial,
    dataFinal: filters.dataFinal,
    ...(filters.statusList?.length > 0 && {
      statusList: filters.statusList.map((s) => s.value),
    }),
  };

  const { data, loading, error, fetchMore } = useQuery(RELATORIO_VENDAS, {
    variables: {
      searchDTO,
      pageableDTO: pageableDTODefault,
    },
    fetchPolicy: 'no-cache',
  });

  const [getRelatorioCsv] = useLazyQuery(RELATORIO_VENDAS_CSV, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const base64Data = data?.relatorioVendasCsv;
      if (!base64Data) return;

      const link = document.createElement('a');
      link.href = `data:text/csv;base64,${base64Data}`;
      link.download = `relatorio-vendas-${moment().format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  });

  const handleDownloadCsv = () => {
    getRelatorioCsv({
      variables: {
        searchDTO,
      },
    });
  };

  const handleLoadMore = () => {
    if (!data?.findAllVenda?.last) {
      fetchMore({
        variables: {
          searchDTO,
          pageableDTO: {
            ...pageableDTODefault,
            pageNumber: (data?.findAllVenda?.number || 0) + 1,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            findAllVenda: {
              ...fetchMoreResult.findAllVenda,
              content: [
                ...prev.findAllVenda.content,
                ...fetchMoreResult.findAllVenda.content,
              ],
            },
          };
        },
      });
    }
  };

  return (
    <Content>
      <Search setFilters={setFilters} filters={filters} />
      <ListContainer>
        <List
          columns={columns}
          items={data?.findAllVenda?.content}
          loading={loading}
          error={error}
          last={data?.findAllVenda?.last}
          onLoadMore={handleLoadMore}
        />
      </ListContainer>
      <ButtonFloat
        title="Download CSV"
        icon={<ArrowDownloadIcon />}
        onClick={handleDownloadCsv}
      />
    </Content>
  );
};

const Content = styled.div`
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
`;

const ListContainer = styled.div`
  flex: 1;
  overflow: auto;
  margin-top: 16px;
`;

export default RelatorioVendas;
